import { useEffect, useRef, type RefObject } from 'react';

/**
 * Comportements d'accessibilité communs à TOUTES les modales maison (`role="dialog"`
 * + `aria-modal="true"`), jusqu'ici recopiés à l'identique dans une vingtaine de
 * fichiers — Tab n'y était intercepté NULLE PART (la tabulation sortait de la modale
 * vers la page d'arrière-plan, qui n'était ni `inert` ni masquée).
 *
 * Le hook prend en charge :
 *  1. la mémorisation puis la RESTAURATION du focus au démontage ;
 *  2. le FOCUS INITIAL (élément fourni, sinon premier focalisable de la modale) ;
 *  3. le VERROU DE DÉFILEMENT du document ;
 *  4. `inert` sur l'ARRIÈRE-PLAN — celui-ci sort de l'ordre de tabulation ET de l'arbre
 *     d'accessibilité (cf. `inertOutsidePath` ci-dessous) ;
 *  5. le PIÈGE DE FOCUS : Tab au dernier élément revient au premier, Shift+Tab au
 *     premier va au dernier ;
 *  6. `Échap` (optionnel — passer `undefined` pendant un appel réseau l'inhibe).
 *
 * RÉGRESSION CORRIGÉE — `inert` posé sur `#root`. L'implémentation précédente inertait
 * la racine applicative (`document.getElementById('root')`). Or la PLUPART des
 * consommateurs rendent leur dialogue À L'INTÉRIEUR de `#root` (seuls quelques-uns
 * passent par `<Portal>`), à commencer par la VISIONNEUSE (`viewer/Viewer.tsx`), qui est
 * un overlay de route et non une modale portée. `inert` atterrissait donc sur un ANCÊTRE
 * du dialogue : le dialogue devenait inerte avec le reste de la page et TOUTE l'interface
 * cessait de répondre (plus de clic, plus de focus, nulle part).
 *
 * Le remède est la technique standard, indépendante de la structure du DOM : on inerte
 * les FRÈRES du dialogue le long de son chemin vers `<body>`, JAMAIS un de ses ancêtres.
 * Elle vaut que le dialogue soit porté hors de `#root` ou rendu dedans.
 *
 * EMPILEMENT : les modales peuvent se superposer (p. ex. la confirmation de suppression
 * de `gallery/ManageSavedFiltersModal.tsx`). Trois mécanismes le rendent sûr :
 *  - `inert` est COMPTÉ PAR ÉLÉMENT (`inertMarks`) : la modale du dessus inerte celle du
 *    dessous (qui est un de ses frères) ; à sa fermeture, seuls les éléments retombés à
 *    zéro redeviennent interactifs — l'arrière-plan, toujours retenu par la modale du
 *    dessous, RESTE inerte ;
 *  - un élément qui portait DÉJÀ `inert` avant toute modale est mémorisé comme tel et ne
 *    se le voit JAMAIS retirer ;
 *  - seule la modale au SOMMET de la pile (`stack`) traite Tab et Échap, sinon les deux
 *    pièges se disputeraient le focus. Le verrou de défilement, lui, reste compté
 *    globalement (`openCount`).
 */

/**
 * `:not([tabindex="-1"])` sur CHAQUE sélecteur, y compris les éléments nativement
 * focalisables — sans quoi un `<button tabindex="-1">` reste compté comme tabulable.
 *
 * Ce n'est pas théorique : les `role="radiogroup"` portent désormais un `tabIndex`
 * MOBILE (un seul radio dans l'ordre de tabulation, les flèches naviguant à
 * l'intérieur — cf. `rovingFocus.ts`), et plusieurs de ces groupes vivent DANS des
 * modales. Sans ce filtre, le piège prenait pour `last` un radio à `tabindex="-1"`,
 * que Tab n'atteint jamais : l'enroulement ne se déclenchait plus et la tabulation
 * s'échappait de la modale.
 */
const FOCUSABLE = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([type="hidden"]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Focalisables de la modale, dans l'ordre du DOM. On écarte ce qui est masqué par
 * ATTRIBUT (`hidden`, `aria-hidden`, `inert`) — pas par mesure de mise en page, qui
 * n'existe pas sous happy-dom et rendrait le piège intestable.
 */
function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) =>
      !el.hasAttribute('hidden') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      el.closest('[inert]') === null,
  );
}

/** Modales actuellement montées, de la plus ancienne à la plus récente. */
const stack: symbol[] = [];
let openCount = 0;
let restoreOverflow = '';

/**
 * Comptage des `inert` POSÉS PAR LE HOOK, par élément.
 *
 * `count` = combien de modales ouvertes retiennent cet élément (empilement) ; `preexisting`
 * = l'élément portait déjà `inert` avant qu'on y touche, auquel cas on ne le lui retire
 * jamais (RESTAURATION FIDÈLE : on ne défait que ce qu'on a fait).
 */
const inertMarks = new Map<Element, { count: number; preexisting: boolean }>();
/** Éléments inertés par chaque modale, pour les relâcher exactement à sa fermeture. */
const inertedByToken = new Map<symbol, Element[]>();

function acquireInert(el: Element): void {
  const mark = inertMarks.get(el);
  if (mark !== undefined) {
    mark.count += 1;
    return;
  }
  const preexisting = el.hasAttribute('inert');
  inertMarks.set(el, { count: 1, preexisting });
  if (!preexisting) el.setAttribute('inert', '');
}

function releaseInert(el: Element): void {
  const mark = inertMarks.get(el);
  if (mark === undefined) return;
  mark.count -= 1;
  if (mark.count > 0) return;
  inertMarks.delete(el);
  if (!mark.preexisting) el.removeAttribute('inert');
}

/**
 * Inerte tout ce qui est HORS du chemin `dialog → <body>` : pour chaque ancêtre, ses
 * enfants autres que celui qui mène au dialogue. Aucun ancêtre du dialogue n'est touché,
 * donc `dialog.closest('[inert]')` reste `null` — le dialogue demeure interactif.
 *
 * Ne couvre volontairement que le DOM présent à l'ouverture : un nœud ajouté ensuite
 * (portail tardif, bandeau) n'est pas inerté, ce qui est le comportement voulu — il peut
 * précisément s'agir de la modale suivante.
 */
function inertOutsidePath(dialog: Element): Element[] {
  const marked: Element[] = [];
  let node: Element = dialog;
  while (node !== document.body) {
    const parent: Element | null = node.parentElement;
    if (parent === null) break; // dialogue détaché du document : rien à inerter
    for (const sibling of Array.from(parent.children)) {
      if (sibling === node) continue;
      acquireInert(sibling);
      marked.push(sibling);
    }
    node = parent;
  }
  return marked;
}

/**
 * `dialog` peut être `null` si la ref n'est pas encore résolue : on garde alors le verrou
 * de défilement (comportement inchangé) mais on n'inerte RIEN — plutôt aucun arrière-plan
 * inerte qu'un arrière-plan inerté au hasard, dialogue compris.
 */
function lockBackground(token: symbol, dialog: Element | null): void {
  openCount += 1;
  if (openCount === 1) {
    restoreOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }
  if (dialog !== null) inertedByToken.set(token, inertOutsidePath(dialog));
}

function unlockBackground(token: symbol): void {
  const marked = inertedByToken.get(token);
  if (marked !== undefined) {
    inertedByToken.delete(token);
    for (const el of marked) releaseInert(el);
  }
  openCount = Math.max(0, openCount - 1);
  if (openCount > 0) return;
  document.body.style.overflow = restoreOverflow;
}

export interface ModalA11yOptions {
  /** Conteneur de la modale (l'élément portant `role="dialog"`). */
  dialogRef: RefObject<HTMLElement | null>;
  /** Élément à focaliser à l'ouverture. À défaut, le premier focalisable de la modale. */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Appelé sur `Échap`. Passer `undefined` (p. ex. pendant un appel réseau) inhibe la
   * fermeture au clavier sans désactiver le piège de focus.
   */
  onEscape?: (() => void) | undefined;
}

export function useModalA11y({ dialogRef, initialFocusRef, onEscape }: ModalA11yOptions): void {
  const tokenRef = useRef<symbol>(undefined);
  // Refs lues au montage seulement — évite de rejouer le focus initial à chaque rendu.
  const dialogRefRef = useRef(dialogRef);
  dialogRefRef.current = dialogRef;
  const initialFocusRefRef = useRef(initialFocusRef);
  initialFocusRefRef.current = initialFocusRef;

  // Montage : focus initial, restauration, verrou d'arrière-plan, entrée dans la pile.
  useEffect(() => {
    const token = Symbol('modal');
    tokenRef.current = token;
    stack.push(token);
    const restoreTo = document.activeElement;

    // L'ORDRE COMPTE : l'inertage a besoin de l'élément du dialogue pour connaître son
    // chemin vers `<body>` — on lit donc la ref AVANT de verrouiller l'arrière-plan.
    const root = dialogRefRef.current.current;
    lockBackground(token, root);

    const target = initialFocusRefRef.current?.current ?? null;
    if (target) target.focus();
    else if (root) focusableWithin(root)[0]?.focus();

    return () => {
      const i = stack.indexOf(token);
      if (i >= 0) stack.splice(i, 1);
      unlockBackground(token);
      if (restoreTo instanceof HTMLElement && restoreTo.isConnected) restoreTo.focus();
    };
  }, []);

  // Clavier : piège de focus + Échap, réservés à la modale au SOMMET de la pile.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (stack[stack.length - 1] !== tokenRef.current) return;
      const root = dialogRef.current;
      if (!root) return;

      if (e.key === 'Escape') {
        if (onEscape) {
          e.preventDefault();
          onEscape();
        }
        return;
      }
      if (e.key !== 'Tab') return;

      const items = focusableWithin(root);
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) {
        // Modale sans aucun focalisable : Tab ne doit pas en sortir non plus.
        e.preventDefault();
        return;
      }
      const active = document.activeElement;

      // Le focus s'est échappé (clic sur le fond, focus resté dans la page) : on le ramène.
      if (!(active instanceof HTMLElement) || !root.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
        return;
      }
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    // `keydown` en CAPTURE : la modale doit voir Tab avant tout gestionnaire local.
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [dialogRef, onEscape]);
}

/** Exposé pour les tests : remet l'état de module à zéro entre deux cas. */
export function __resetModalA11yForTests(): void {
  stack.length = 0;
  openCount = 0;
  restoreOverflow = '';
  document.body.style.overflow = '';
  for (const [el, mark] of inertMarks) {
    if (!mark.preexisting) el.removeAttribute('inert');
  }
  inertMarks.clear();
  inertedByToken.clear();
}
